import { useState } from 'react';

/**
 * Datos de receta de una línea del carrito (modo comercial, artículo con prescripción y cliente
 * cuyo tipo de cliente receta tiene "Alerta Receta"): especie (código CIMAVet de BC), nº de
 * animales y nº de crotal. Todos opcionales; viajan a la línea de venta de BC al lanzar el pedido.
 *
 * Plegado muestra el resumen; desplegado, el formulario. Las especies se piden al abrir
 * (solo las compatibles con el artículo, o todas si el artículo no tiene ninguna).
 */
export const resumenReceta = (product) => {
  const partes = [];
  if (product.recetaEspecie) partes.push(product.recetaEspecieNombre || product.recetaEspecie);
  if (product.recetaNumAnimales !== null && product.recetaNumAnimales !== undefined) {
    partes.push(`${product.recetaNumAnimales} ${product.recetaNumAnimales === 1 ? 'animal' : 'animales'}`);
  }
  if (product.recetaAnimalId) partes.push(`crotal ${product.recetaAnimalId}`);
  return partes.join(' · ');
};

export const RecetaLineaForm = ({ product, onSave, loadEspecies }) => {
  const [abierto, setAbierto] = useState(false);
  const [especies, setEspecies] = useState(null);
  const [especie, setEspecie] = useState(product.recetaEspecie || '');
  const [numAnimales, setNumAnimales] = useState(
    product.recetaNumAnimales === null || product.recetaNumAnimales === undefined ? '' : String(product.recetaNumAnimales));
  const [animalId, setAnimalId] = useState(product.recetaAnimalId || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const resumen = resumenReceta(product);

  const abrir = async () => {
    setError(null);
    setEspecie(product.recetaEspecie || '');
    setNumAnimales(product.recetaNumAnimales === null || product.recetaNumAnimales === undefined ? '' : String(product.recetaNumAnimales));
    setAnimalId(product.recetaAnimalId || '');
    setAbierto(true);
    if (especies === null) {
      try {
        setEspecies(await loadEspecies(product));
      } catch (e) {
        setEspecies([]);
        setError('No se han podido cargar las especies');
      }
    }
  };

  const guardar = async (datos) => {
    setGuardando(true);
    setError(null);
    try {
      await onSave(product, datos);
      setAbierto(false);
    } catch (e) {
      setError(e?.message || 'No se han podido guardar los datos de receta');
    } finally {
      setGuardando(false);
    }
  };

  const enviar = (e) => {
    e.preventDefault();
    const n = numAnimales === '' ? null : Number(numAnimales);
    if (n !== null && (!Number.isInteger(n) || n < 0)) {
      setError('El número de animales debe ser un entero mayor o igual que 0');
      return;
    }
    guardar({ especie: especie || null, numAnimales: n, animalId: animalId.trim() || null });
  };

  if (!abierto) {
    return (
      <div className="d-flex flex-row align-items-center gap-2 mt-2 flex-wrap" data-testid="receta-resumen">
        <span className="badge bg-warning text-dark">Receta</span>
        {resumen ? <span className="small">{resumen}</span> : <span className="small text-muted">Sin datos de receta</span>}
        <button type="button" className="btn btn-link btn-sm p-0" onClick={abrir}>
          {resumen ? 'Editar datos de receta' : 'Añadir datos de receta'}
        </button>
      </div>
    );
  }

  return (
    <form className="border rounded p-2 mt-2 bg-light" onSubmit={enviar} data-testid="receta-form">
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="badge bg-warning text-dark">Receta</span>
        <span className="small">Datos opcionales para la línea (viajan al pedido de BC)</span>
      </div>
      <div className="row g-2 align-items-end">
        <div className="col-12 col-md-4">
          <label className="form-label small mb-0" htmlFor={`receta-especie-${product.id}`}>Especie</label>
          <select id={`receta-especie-${product.id}`} className="form-select form-select-sm" value={especie}
                  onChange={(e) => setEspecie(e.target.value)} disabled={especies === null}>
            <option value="">{especies === null ? 'Cargando…' : 'Sin especificar'}</option>
            {(especies || []).map(esp => (
              <option key={esp.codigo} value={esp.codigo}>{esp.etiqueta || esp.nombre}</option>
            ))}
          </select>
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small mb-0" htmlFor={`receta-num-${product.id}`}>Nº de animales</label>
          <input id={`receta-num-${product.id}`} type="number" min="0" step="1" className="form-control form-control-sm"
                 value={numAnimales} onChange={(e) => setNumAnimales(e.target.value)} />
        </div>
        <div className="col-6 col-md-5">
          <label className="form-label small mb-0" htmlFor={`receta-crotal-${product.id}`}>Nº de crotal</label>
          <input id={`receta-crotal-${product.id}`} type="text" maxLength="2048" className="form-control form-control-sm"
                 value={animalId} onChange={(e) => setAnimalId(e.target.value)} placeholder="ES0123456789" />
        </div>
      </div>
      {error && <div className="text-danger small mt-2">{error}</div>}
      <div className="d-flex gap-2 justify-content-end mt-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAbierto(false)} disabled={guardando}>
          Cancelar
        </button>
        {resumen && (
          <button type="button" className="btn btn-outline-danger btn-sm" disabled={guardando}
                  onClick={() => guardar({ especie: null, numAnimales: null, animalId: null })}>
            Quitar
          </button>
        )}
        <button type="submit" className="btn btn-info btn-sm" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};
